import React, { useState } from "react";

import { InputChangeEvent } from "api/model/airflow-task/task-template/TaskTemplateInfo";
import { IPair } from "models/dataset-models";
import { Button } from "primereact/button";
import { Column, ColumnBodyOptions, ColumnEditorOptions } from "primereact/column";
import { confirmPopup } from "primereact/confirmpopup";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";
import { ScheduledTaskDTO } from "../../../../../api/model/airflow-task/ScheduledTaskDTO";

interface IConstantMapTableProps {
  property: keyof ScheduledTaskDTO;
  constants: Record<string, string>;
  onChange: (e: InputChangeEvent<Record<string, string>>) => void;
}

interface IConstRow {
  key: string;
  value: string;
  editMode: boolean;
}

export default function TaskConfigConstantMapTable(props: IConstantMapTableProps) {
  const [constArr, setConstArr] = useState(mapToList(props.constants));

  const propagateChangesStateUp = (arr: IPair[]) => {
    const c: Record<string, string> = {};

    arr.forEach(e => {
      c[e.key] = e.value;
    });

    props.onChange({
      property: props.property,
      value: c,
    });
  };

  const confirmWrapper = (event: any, confirmHandler: any) => {
    confirmPopup({
      target: event.currentTarget,
      message: T("confirm-popup.are-you-really-want-to-delete-this-constant"),
      icon: "pi pi-exclamation-triangle",
      accept: confirmHandler,
    });
  };

  const handleConstAdd = () => {
    const newRow = { key: "new_key_value", value: "some_value" } as IConstRow;
    setConstArr(prev => {
      const updated = [...prev, newRow];
      propagateChangesStateUp(updated);
      return updated;
    });
  };

  const handleConstDelete = (index: number) => {
    setConstArr(prev => {
      const filtered = prev.toSpliced(index, 1);
      propagateChangesStateUp(filtered);
      return filtered;
    });
  };

  const headerTemplate = (
    <div className="flex justify-content-between">
      <h4>{T("word.constants")}</h4>
      <Button icon="pi pi-plus" className="p-button-success" onClick={handleConstAdd} />
    </div>
  );

  const keyEditorTemplate = (opt: ColumnEditorOptions) => {
    return (
      <InputText
        placeholder={T("word.key")}
        value={opt.rowData.key || ""}
        onChange={e => opt.editorCallback?.(e.target.value)}
      />
    );
  };

  const valueEditorTemplate = (opt: ColumnEditorOptions) => {
    return (
      <InputText
        placeholder={T("word.value")}
        value={opt.rowData.value || ""}
        onChange={e => opt.editorCallback?.(e.target.value)}
      />
    );
  };

  const renderDeleteButton = (_: IConstRow, options: ColumnBodyOptions) => {
    return (
      <Button
        icon="pi pi-trash"
        className="const-del-btn  p-button-rounded p-button-text p-button-danger"
        onClick={e => confirmWrapper(e, () => handleConstDelete(options.rowIndex))}
      />
    );
  };

  const handleRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    const newData = e.newData as IConstRow;

    setConstArr(prev => {
      const updated = prev.map((item, index) => (index === e.index ? newData : item));
      propagateChangesStateUp(updated);
      return updated;
    });
  };

  return (
    <DataTable
      value={constArr}
      editMode="row"
      onRowEditComplete={handleRowEditComplete}
      header={headerTemplate}
      width="100%"
      size="small"
    >
      <Column field="key" header={T("word.key")} editor={keyEditorTemplate} style={{ width: "20rem" }} />
      <Column field="value" header={T("word.value")} editor={valueEditorTemplate} />
      <Column rowEditor align="right" className="w-5rem p-0" />
      <Column body={renderDeleteButton} className="w-3rem" />
    </DataTable>
  );
}

function mapToList(map: Record<string, string>): IConstRow[] {
  return Object.keys(map).map(k => {
    return { key: k, value: map[k] } as IConstRow;
  });
}
